'use client';

import { type PostFeedInput } from '@shared/all/validators';
import { useDatabaseContext } from '@shared/utils/context/DatabaseContext';
import { useUserIdentity } from '@shared/utils/context/UserContext';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import useNavigator from '@shared/utils/hooks/useNavigator';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Error from '../common/Error';
import DatabaseGuard from '../database/DatabaseGuard';
import PostItem from './PostItem';
import PostSkeletonLoading from './PostSkeletonLoading';
import ReplyItem from './ReplyItem';
type Props = {
  params: PostFeedInput;
  isFullLength: boolean;
  disableLink: boolean;
  disableTracking?: boolean;
  manualLoading?: boolean;
  hideEndReachedMessage?: boolean;
  uniqueId?: string;
  factiiiIds?: number[];
  useContainerVirtualizer?: boolean;
};
const PostMode = (props: Props) => {
  const {
    localPreferenceState,
    getPostParams
  } = useLocalPreference();
  const currentUser = useUserIdentity();
  const {
    navigatorState
  } = useNavigator();
  const {
    queryKey,
    isNsfw,
    isPolitics,
    fetchPrivatePosts
  } = usePostsShared(client, props.uniqueId);
  const {
    state: {
      db
    }
  } = useDatabaseContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const isPrivateMode = localPreferenceState.postModeType === 'private';
  // Distance from the top of the page to the start of the list. The window
  // virtualizer needs this so its position math accounts for anything rendered
  // above the list (e.g. the profile header); without it the top posts unmount
  // while scrolling. Stays 0 for the container virtualizer.
  const [scrollMargin, setScrollMargin] = useState(0);
  const {
    data,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    error
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({
      pageParam
    }) => {
      if (isPrivateMode) {
        // Private mode uses page-based pagination (local SQLite)
        const page = typeof pageParam === 'number' ? pageParam : 0;
        const result = await fetchPrivatePosts({
          page
        });
        return {
          posts: result.posts,
          isPrivate: true
        };
      }

      // Regular mode uses cursor-based pagination (backend)
      const {
        posts
      } = await unwrapFromResult(client.posts.feed.query(getPostParams(props.params, pageParam as string | undefined, props.factiiiIds)));
      return {
        posts,
        isPrivate: false
      };
    },
    initialPageParam: isPrivateMode ? 0 : undefined as string | number | undefined,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const posts_0 = lastPage.posts;
      if (posts_0.length < 10) return undefined;
      // Private mode uses page numbers, regular mode uses cursor (post id)
      if (lastPage.isPrivate) {
        return (lastPageParam as number) + 1;
      }
      return posts_0[posts_0.length - 1]?.id;
    },
    refetchOnWindowFocus: false,
    enabled: currentUser.isAuthenticated !== null && (!isPrivateMode || !!db)
  });
  const allPosts = data?.pages.flatMap(page_0 => page_0.posts) || [];
  // Cursor pagination over volatile sorts (trending/popular) or a realtime insert
  // can surface the same post across page boundaries — dedupe by id so a post never
  // renders twice (and to avoid duplicate React keys).
  const seenPostIds = new Set<string>();
  const filteredPosts = allPosts.filter(post => !seenPostIds.has(post.id) && seenPostIds.add(post.id));
  const containerVirtualizer = useVirtualizer({
    count: filteredPosts.length,
    estimateSize: () => 400,
    overscan: 1,
    getScrollElement: () => containerRef.current,
    enabled: !!props.useContainerVirtualizer,
    measureElement: el => el.getBoundingClientRect().height
  });
  const windowVirtualizer = useWindowVirtualizer({
    count: filteredPosts.length,
    overscan: 1,
    estimateSize: () => 400,
    enabled: !props.useContainerVirtualizer,
    scrollMargin,
    measureElement: el_0 => el_0.getBoundingClientRect().height
  });
  const virtualizer = props.useContainerVirtualizer ? containerVirtualizer : windowVirtualizer;
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems.at(-1);

  // Measure the list's offset from the top of the document for the window
  // virtualizer's scrollMargin. Re-measures on resize and as posts load (when
  // the content above settles). Document-relative so it's correct regardless of
  // the offset parent. useLayoutEffect (matching react-virtual's window example)
  // so the offset is applied before paint — no position flash on mount.
  useLayoutEffect(() => {
    if (props.useContainerVirtualizer) return;
    const measure = () => {
      const el_1 = containerRef.current;
      if (el_1) setScrollMargin(el_1.getBoundingClientRect().top + window.scrollY);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [props.useContainerVirtualizer, filteredPosts.length]);
  useEffect(() => {
    if (lastVirtualItem && lastVirtualItem.index >= filteredPosts.length - 3 && !props.manualLoading && !isFetching && !isFetchingNextPage && hasNextPage) {
      void fetchNextPage();
    }
  }, [virtualItems, lastVirtualItem, filteredPosts.length, props.manualLoading, isFetching, isFetchingNextPage, hasNextPage]);
  const postContent = <>
      {error && navigatorState.isOnline && <Error error={error} loading={false} message="Something went wrong while loading content! Please try again by refreshing." />}
      {(!error || !navigatorState.isOnline) && <div ref={containerRef} className={props.useContainerVirtualizer ? 'max-h-[80vh] overflow-y-auto py-4' : ''}>
          {/* Render Posts */}
          <div style={{
        height: `${virtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative'
      }}>
            {virtualItems.map(virtualItem => {
          const post_0 = filteredPosts[virtualItem.index];
          if (!post_0) return null;

          // Merge consecutive posts from the same user (headless rendering).
          // Never merge if either post is anonymous — that would reveal authorship.
          const priorPost = virtualItem.index > 0 ? filteredPosts[virtualItem.index - 1] : null;
          const isHeadless = !!priorPost && !priorPost.user.anonymous && !post_0.user.anonymous && priorPost.user.id === post_0.user.id;
          return <div key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItem.start - (virtualizer.options.scrollMargin ?? 0)}px)`
          }}>
                  <div data-post-id={post_0.id} className={'mb-4'}>
                    <PostItem isFullLength={props.isFullLength} post={post_0} spaceSlug={props.params?.source === 'space' ? props.params.spaceSlug : undefined} disableLink={props.disableLink} isInView disableTracking={props.disableTracking} uniqueId={props.uniqueId} isHeadless={isHeadless} />
                    {post_0.replies && post_0.replies.length > 0 && post_0.replies.map(reply => <div key={reply.id} className="rounded-md bg-background-secondary p-4 shadow-lg">
                          <ReplyItem disableLink={false} isFullLength={false} post={reply} isPoliticsParent={isPolitics(post_0.postFactiiisCount || [], post_0.space)} isNsfwParent={isNsfw(post_0.postFactiiisCount || [])} currentLevel={0} />
                        </div>)}
                  </div>
                </div>;
        })}
          </div>
          {/* Message for when end is reached */}
          {!props.hideEndReachedMessage && !hasNextPage && !isFetching && <div className="flex w-full flex-col items-center space-y-2 p-4 text-center text-textSecondary/70">
              <CheckCircle2 />
              <p className="text-textSecondary/70">
                You&apos;re all caught up.
              </p>
            </div>}
          {/* Observer for loading next page */}
          {!props.manualLoading && hasNextPage && <div className="flex flex-col justify-center space-y-4">
              <PostSkeletonLoading />
              <PostSkeletonLoading />
              <PostSkeletonLoading />
            </div>}
          {props.manualLoading && <div className="flex items-center gap-x-2 p-4">
              <div className="flex items-center space-x-1 font-semibold">
                <p className="mt-1">{filteredPosts.length}</p>
                <p>{filteredPosts.length === 1 ? 'post' : 'posts'} loaded</p>
              </div>

              {hasNextPage && <>
                  •
                  <Button text={isFetchingNextPage ? '' : 'Load More'} variant="SECONDARY" loading={isFetchingNextPage} onClick={() => {
            void fetchNextPage();
            return;
          }} disabled={!hasNextPage || isFetchingNextPage || isFetching} classNames="!p-0 hover:bg-transparent !text-primary hover:underline" />
                </>}
            </div>}
        </div>}
    </>;
  if (isPrivateMode) {
    return <DatabaseGuard title="Private Posts Unavailable" description="Private posts are stored locally and require the database to be enabled.">
        {postContent}
      </DatabaseGuard>;
  }
  return postContent;
};
export default PostMode;